/** Manual Bookings panel — Blueprint Batch B.
 *
 *  Lets the campaign owner declare a "spot window" — a window (start_at, end_at)
 *  during which they expect their ad to air on a specific station. The MISSED-
 *  window scheduler watches the window and writes a MISSED ad_detection if no
 *  airing was detected.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { bus, BUS } from "@/lib/eventBus";
import { Button, EmptyState, Pill } from "@/design";
import { color, radius, type, font } from "@/design/tokens";

const ymdLocal = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const STATUS_PILL = {
  scheduled: { variant: "accent",  label: "Scheduled" },
  aired:     { variant: "success", label: "Aired" },
  missed:    { variant: "danger",  label: "Missed" },
};

export function AdVerifyBookings({ campaign, stations = [], "data-testid": tid }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const now = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => {
    const d = new Date(now.getTime() + 5 * 60 * 1000);
    d.setSeconds(0, 0);
    return ymdLocal(d);
  }, [now]);
  const defaultEnd = useMemo(() => {
    const d = new Date(now.getTime() + 35 * 60 * 1000);
    d.setSeconds(0, 0);
    return ymdLocal(d);
  }, [now]);

  const [form, setForm] = useState({
    station_id: "",
    ad_title: campaign?.ad_title || campaign?.brand || "",
    start_at: defaultStart,
    end_at: defaultEnd,
    expected_spots: 1,
    notes: "",
  });

  const refresh = async () => {
    if (!campaign?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/campaigns/${campaign.id}/bookings`);
      setBookings(data.bookings || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [campaign?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh the bookings list when a MISSED/AIRED ad_detection arrives
  // via SSE — the missed-window scheduler flips a booking's status, so the
  // pills here must follow. Scoped to this campaign to avoid stray refetches.
  useEffect(() => {
    if (!campaign?.id) return undefined;
    return bus.on(BUS.AD_DETECTION, (ev) => {
      if (!ev || ev.campaign_id !== campaign.id) return;
      if (ev.status === "MISSED" || ev.method === "booking-window") refresh();
    });
  }, [campaign?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!form.station_id && stations[0]) setForm((f) => ({ ...f, station_id: stations[0].id }));
  }, [stations, form.station_id]);

  const save = async () => {
    if (!form.station_id) return toast.error("Pick a station");
    if (!form.ad_title) return toast.error("Ad title required");
    setSaving(true);
    const t = toast.loading("Recording booking…");
    try {
      const body = {
        station_id: form.station_id,
        ad_title: form.ad_title,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        expected_spots: Number(form.expected_spots) || 1,
        notes: form.notes,
      };
      await api.post(`/campaigns/${campaign.id}/bookings`, body);
      toast.success("Booking saved", { id: t, description: "MISSED row will be auto-written if no airing is detected in the window." });
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error("Could not save", { id: t, description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete booking for ${b.station_name} — ${b.ad_title}?`)) return;
    try {
      await api.delete(`/campaigns/${campaign.id}/bookings/${b.id}`);
      toast.success("Booking deleted");
      refresh();
    } catch (e) {
      toast.error("Delete failed", { description: e.response?.data?.detail || e.message });
    }
  };

  const inp = {
    width: "100%", padding: "9px 12px", borderRadius: radius.sm,
    background: color.navy900, border: `1px solid ${color.line}`,
    color: "#fff", fontSize: 13, fontFamily: font.sans, outline: "none",
  };
  const lbl = { ...type.micro, color: color.slate400, marginBottom: 4, display: "block" };

  return (
    <section data-testid={tid || "adverify-bookings"} style={{ fontFamily: font.sans }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <h3 style={{ ...type.h3, color: "#fff", margin: 0 }}>📅 Bookings ({bookings.length})</h3>
        <Button data-testid="bookings-add-btn" size="sm" variant="primary" audience="agency"
          onClick={() => setOpen((x) => !x)}>
          {open ? "Cancel" : "+ Add booking"}
        </Button>
      </div>

      {open && (
        <div data-testid="bookings-form" style={{
          background: color.navy800, border: `1px solid ${color.line}`,
          borderRadius: radius.md, padding: 16, marginBottom: 12,
          display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12,
        }}>
          <div>
            <label style={lbl}>Station</label>
            <select data-testid="booking-station" style={inp} value={form.station_id}
              onChange={(e) => setForm({ ...form, station_id: e.target.value })}>
              {stations.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.is_online ? "🟢" : "⚫"} {s.name} {s.frequency ? `· ${s.frequency}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={lbl}>Ad title (the text the crawler matches)</label>
            <input data-testid="booking-title" style={inp} value={form.ad_title}
              onChange={(e) => setForm({ ...form, ad_title: e.target.value })} />
          </div>
          <div>
            <label style={lbl}>Window starts</label>
            <input data-testid="booking-start" type="datetime-local" style={inp} value={form.start_at}
              onChange={(e) => setForm({ ...form, start_at: e.target.value })} />
          </div>
          <div>
            <label style={lbl}>Window ends</label>
            <input data-testid="booking-end" type="datetime-local" style={inp} value={form.end_at}
              onChange={(e) => setForm({ ...form, end_at: e.target.value })} />
          </div>
          <div>
            <label style={lbl}>Expected spots</label>
            <input data-testid="booking-spots" type="number" min="1" style={inp} value={form.expected_spots}
              onChange={(e) => setForm({ ...form, expected_spots: e.target.value })} />
          </div>
          <div>
            <label style={lbl}>Notes (optional)</label>
            <input data-testid="booking-notes" style={inp} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div style={{ gridColumn: "1/-1", display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Button data-testid="booking-cancel" size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="booking-save" size="sm" variant="primary" audience="agency"
              loading={saving} onClick={save}>Save booking</Button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ color: color.slate400, fontSize: 12 }}>Loading…</div>
      ) : bookings.length === 0 ? (
        <EmptyState tone="dark"
          title="No bookings yet"
          helper="Declare a spot window above. If no airing is detected before the window closes, a MISSED row drops into the exception lane."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {bookings.map((b) => {
            const p = STATUS_PILL[b.status] || STATUS_PILL.scheduled;
            const start = new Date(b.start_at).toLocaleString("en-GH",
              { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
            const end = new Date(b.end_at).toLocaleTimeString("en-GH",
              { hour: "2-digit", minute: "2-digit" });
            return (
              <div key={b.id} data-testid={`booking-${b.id}`} style={{
                display: "grid", gridTemplateColumns: "1fr 1fr auto auto auto",
                gap: 14, alignItems: "center",
                padding: "10px 14px", borderRadius: radius.sm,
                background: color.navy800, border: `1px solid ${color.line}`,
              }}>
                <div>
                  <div style={{ color: "#fff", fontSize: 13, fontWeight: 600 }}>{b.station_name}</div>
                  <div style={{ color: color.slate400, fontSize: 11 }}>{b.ad_title}</div>
                </div>
                <div style={{ color: color.slate400, fontSize: 12 }}>
                  {start} → {end}
                </div>
                <div style={{ ...type.number, color: color.cyan600, fontSize: 13 }}>×{b.expected_spots}</div>
                <Pill variant={p.variant}>{p.label}</Pill>
                <Button data-testid={`booking-delete-${b.id}`} size="sm" variant="ghost"
                  onClick={() => remove(b)}>Delete</Button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
